import React from 'react';
import { WebsiteDesignProps } from './types';
import { Button } from "@/components/ui/button";
import { BookOpen, Search, Menu, Building2, Ticket, Calendar } from 'lucide-react';

const UniversityOfPennsylvania: React.FC<WebsiteDesignProps> = ({ previewData }) => {
    const schoolName = previewData?.name || "University of Pennsylvania";
    const shortName = schoolName.includes("Pennsylvania") ? "Penn" : schoolName.split(' ')[0];

    return (
        <div className="min-h-screen bg-white font-sans text-[#011F5B] selection:bg-[#990000] selection:text-white">
            {/* Split Top Bar */}
            <div className="bg-[#011F5B] text-white py-2 px-6 flex justify-between items-center text-xs font-bold uppercase tracking-widest">
                <div className="flex gap-6">
                    <a href="#" className="hover:text-[#990000]">Faculty</a>
                    <a href="#" className="hover:text-[#990000]">{shortName} Staff</a>
                    <a href="#" className="hover:text-[#990000]">Students</a>
                    <a href="#" className="hover:text-[#990000]">Alumni</a>
                </div>
                <div>
                   <a href="#" className="hover:text-[#990000] transition-colors">Access {shortName}</a>
                </div>
            </div>

            {/* Main Nav */}
            <nav className="bg-white px-6 py-6 border-b border-gray-200">
                <div className="max-w-[1400px] mx-auto flex justify-between items-center">
                    <div className="flex items-center gap-4">
                        <div className="w-14 h-14 bg-[#011F5B] text-white flex items-center justify-center font-serif text-3xl font-bold rounded-sm border-b-4 border-[#990000]">
                            P
                        </div>
                        <div className="flex flex-col">
                            <h1 className="text-2xl font-serif font-bold leading-none tracking-tight text-[#011F5B]">{schoolName.split(' ')[0] || "Penn"}</h1>
                            <span className="text-xs font-bold uppercase tracking-widest text-[#990000]">{schoolName}</span>
                        </div>
                    </div>

                    <div className="hidden lg:flex gap-8 font-sans font-bold text-[#011F5B]">
                        <a href="#" className="hover:text-[#990000] transition-colors">About</a>
                        <a href="#" className="hover:text-[#990000] transition-colors">Academics</a>
                        <a href="#" className="hover:text-[#990000] transition-colors">Admissions</a>
                        <a href="#" className="hover:text-[#990000] transition-colors">Research</a>
                        <a href="#" className="hover:text-[#990000] transition-colors">Life at {shortName}</a>
                    </div>

                    <div className="flex gap-4">
                        <Search size={24} className="text-[#011F5B] cursor-pointer" />
                        <Menu size={24} className="text-[#011F5B] cursor-pointer lg:hidden" />
                    </div>
                </div>
            </nav>

            {/* Hero - Split Overlay */}
            <header className="relative h-[650px] overflow-hidden">
                <img 
                    src="https://images.unsplash.com/photo-1541339907198-e08756dedf3f?q=80&w=2070&auto=format&fit=crop" 
                    alt="Penn Campus" 
                    className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-[#011F5B]/90 to-transparent w-3/4"></div>
                
                <div className="absolute top-1/2 -translate-y-1/2 left-0 w-full px-8 md:px-12">
                    <div className="max-w-[1400px] mx-auto text-white">
                        <span className="font-bold text-[#990000] uppercase tracking-widest text-sm mb-4 block bg-white w-fit px-2 py-1">In Focus</span>
                         <h1 className="text-5xl md:text-7xl font-serif font-bold mb-8 max-w-2xl leading-tight">
                             Innovating for a better future.
                         </h1>
                         <p className="text-xl max-w-xl mb-10 text-blue-100 font-light leading-relaxed">
                             Benjamin Franklin founded {shortName} to adapt the classics to the practical needs of the era. Today, we continue that tradition of applying knowledge to solve complex problems.
                         </p>
                         <Button className="bg-[#990000] hover:bg-[#b30000] text-white font-bold uppercase tracking-widest px-8 h-12 rounded-sm border-2 border-[#990000] hover:border-white transition-colors">
                             Read the Vision
                         </Button>
                    </div>
                </div>
            </header>

            {/* Quick Links / Tiles */}
            <section className="bg-[#F2F2F2] py-24 px-8">
                <div className="max-w-[1400px] mx-auto">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        <div className="bg-white p-10 border-t-8 border-[#011F5B] hover:-translate-y-2 transition-transform duration-300 shadow-sm cursor-pointer group">
                             <h3 className="text-2xl font-serif font-bold text-[#011F5B] mb-4 group-hover:text-[#990000]">Undergraduate Admissions</h3>
                             <p className="text-gray-600 mb-6 leading-relaxed">
                                 Join a community of students who are driven to make a difference. Four undergraduate schools, one campus.
                             </p>
                             <span className="font-bold text-[#990000] uppercase text-xs tracking-widest hover:underline">Apply Now &rarr;</span>
                        </div>
                         <div className="bg-white p-10 border-t-8 border-[#990000] hover:-translate-y-2 transition-transform duration-300 shadow-sm cursor-pointer group">
                             <h3 className="text-2xl font-serif font-bold text-[#011F5B] mb-4 group-hover:text-[#990000]">Graduate & Professional</h3>
                             <p className="text-gray-600 mb-6 leading-relaxed">
                                 World-class programs in law, medicine, business, education, and more.
                             </p>
                             <span className="font-bold text-[#990000] uppercase text-xs tracking-widest hover:underline">Explore Programs &rarr;</span>
                        </div>
                         <div className="bg-white p-10 border-t-8 border-[#011F5B] hover:-translate-y-2 transition-transform duration-300 shadow-sm cursor-pointer group">
                             <h3 className="text-2xl font-serif font-bold text-[#011F5B] mb-4 group-hover:text-[#990000]">{shortName} Global</h3>
                             <p className="text-gray-600 mb-6 leading-relaxed">
                                 Bringing the world to {shortName} and {shortName} to the world through research and engagement.
                             </p>
                             <span className="font-bold text-[#990000] uppercase text-xs tracking-widest hover:underline">Learn More &rarr;</span>
                        </div>
                    </div>
                </div>
            </section>

            {/* Penn Today News */}
            <section className="py-24 px-8 max-w-[1400px] mx-auto">
                <h2 className="text-4xl font-serif text-[#011F5B] mb-12 text-center font-bold">{shortName} Today</h2>
                
                <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
                    <div className="lg:col-span-2 relative group cursor-pointer">
                        <img src="https://images.unsplash.com/photo-1562774053-701939374585?q=80&w=2086&auto=format&fit=crop" className="w-full h-full object-cover min-h-[400px]" alt="Main News" />
                        <div className="absolute bottom-0 left-0 bg-gradient-to-t from-[#011F5B] to-transparent w-full p-8 pt-24 text-white">
                            <h3 className="text-2xl font-bold font-serif leading-tight mb-2 group-hover:underline decoration-[#990000] underline-offset-4">
                                Celebrating the Class of 2026 at Convocation
                            </h3>
                            <p className="text-sm font-sans mb-0">University Life • Sept 28</p>
                        </div>
                    </div>

                    <div className="lg:col-span-2 grid grid-rows-3 gap-8">
                         {[
                             { cat: "Innovation", title: "Engineering team designs new battery tech", date: "Sept 27" },
                             { cat: "Health", title: `${shortName} Medicine study links sleep patterns to heart health`, date: "Sept 26" },
                             { cat: "Arts", title: "ICA exhibition explores digital landscapes", date: "Sept 25" }
                         ].map((item, i) => (
                             <div key={i} className="flex gap-6 border-b border-gray-100 pb-8 last:border-0 last:pb-0 cursor-pointer group">
                                 <div className="w-32 h-full bg-gray-200 shrink-0"></div>
                                 <div>
                                     <span className="text-[#990000] font-bold text-xs uppercase tracking-widest mb-1 block">{item.cat}</span>
                                     <h4 className="text-lg font-bold text-[#011F5B] leading-tight group-hover:underline">{item.title}</h4>
                                     <span className="text-gray-400 text-xs mt-2 block">{item.date}</span>
                                 </div>
                             </div>
                         ))}
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="bg-[#011F5B] text-white py-16 px-8">
                <div className="max-w-[1400px] mx-auto grid grid-cols-1 md:grid-cols-4 gap-12">
                     <div className="md:col-span-1">
                        <h2 className="font-serif font-bold text-3xl mb-6">{shortName}</h2>
                        <div className="text-sm space-y-2 opacity-80">
                            <p>{schoolName}</p>
                            <p>Philadelphia, PA 19104</p>
                            <p>(215) 898-5000</p>
                        </div>
                     </div>

                     <div className="md:col-span-3 grid grid-cols-2 md:grid-cols-3 gap-8 text-sm font-bold opacity-90">
                         <ul>
                             <li className="mb-4 text-[#990000] uppercase tracking-widest">Connect</li>
                             <li><a href="#" className="hover:text-[#990000]">Contact Us</a></li>
                             <li><a href="#" className="hover:text-[#990000]">Directories</a></li>
                             <li><a href="#" className="hover:text-[#990000]">Maps</a></li>
                         </ul>
                         <ul>
                             <li className="mb-4 text-[#990000] uppercase tracking-widest">Resources</li>
                             <li><a href="#" className="hover:text-[#990000]">My{shortName}</a></li>
                             <li><a href="#" className="hover:text-[#990000]">Canvas</a></li>
                             <li><a href="#" className="hover:text-[#990000]">Path@{shortName}</a></li>
                         </ul>
                         <ul>
                             <li className="mb-4 text-[#990000] uppercase tracking-widest">Legal</li>
                             <li><a href="#" className="hover:text-[#990000]">Disclaimer</a></li>
                             <li><a href="#" className="hover:text-[#990000]">Privacy Policy</a></li>
                             <li><a href="#" className="hover:text-[#990000]">Accessibility</a></li>
                         </ul>
                     </div>
                </div>
                <div className="max-w-[1400px] mx-auto mt-16 pt-8 border-t border-[#022b7a] text-center text-xs opacity-60">
                    Copyright © 2026 {schoolName}
                </div>
            </footer>
        </div>
    );
};

export default UniversityOfPennsylvania;
